/**
 * R4 — Persistent pins rail. Always-visible right column on the operator
 * workspace. Per CD spec:
 *   - Typed pins (country / outbreak / signal / pathogen) with different
 *     decay rates per type — icon carries the type.
 *   - Stale pins MUST visibly flag (hourglass + tooltip with age).
 *   - Pins added from the Inbox via 📌 verb append to the first watchlist.
 *   - "Build briefing →" CTA at the bottom — triggers the R6 modal.
 *   - Share-watchlist hint (by link / shared workspace) — Phase 7 wiring.
 */

import { useMemo } from 'react';
import { FamilyGlyph, PinChip } from './primitives';
import {
  TIER_META, type Watchlist, type Pin, type InboxItem, type PathogenFamily,
} from './types';
import { FAM, IDC } from './sample-data';

export interface PinsRailProps {
  watchlists: readonly Watchlist[];
  /** Pins added during this session via the Inbox 📌 verb. Prepended to
   *  the first watchlist for the live demo. In production these would
   *  persist server-side per-user. */
  sessionPins: readonly InboxItem[];
  onOpenBriefing: () => void;
}

export function PinsRail({ watchlists, sessionPins, onOpenBriefing }: PinsRailProps) {
  const all = useMemo(() => {
    const base = watchlists.map(wl => ({ ...wl, pins: [...wl.pins] }));
    if (sessionPins.length > 0 && base[0]) {
      const fromSession: Pin[] = sessionPins.map((it, i) => ({
        id: `session-${i}`,
        type: 'outbreak',
        label: `${it.common} · ${it.admin.split(',')[0]}`,
        pathogen: it.pathogen,
        tier: it.tier,
        stale: !!it.dataQuality,
        asOfDays: 0,
      }));
      base[0].pins = [...fromSession, ...base[0].pins];
    }
    return base;
  }, [watchlists, sessionPins]);

  const total = all.reduce((n, wl) => n + wl.pins.length, 0);

  return (
    <aside
      aria-label="Watchlists"
      style={{
        width: 280, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px',
        borderBottom: '1px solid var(--bm-border)',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
          letterSpacing: '.4px', textTransform: 'uppercase',
          color: 'var(--bm-text-muted)',
        }}>
          <span style={{ color: 'var(--bm-pin)' }}>📌</span> Watchlists
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)',
        }}>{total}</span>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
        {all.map(wl => (
          <div key={wl.id} style={{ marginBottom: 14 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              padding: '4px 6px', marginBottom: 6,
              fontSize: 12, fontWeight: 600, color: 'var(--bm-text-muted)',
            }}>
              {wl.name}
              <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
                {wl.pins.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {wl.pins.map(pin => (
                <PinChipWithGlyph key={pin.id} pin={pin} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <footer style={{
        padding: '10px 12px', borderTop: '1px solid var(--bm-border)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <button
          type="button"
          onClick={onOpenBriefing}
          style={{
            padding: '6px 12px', borderRadius: 5, cursor: 'pointer',
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
            background: 'var(--bm-accent, #6ea3ff)',
            color: 'var(--bm-bg)',
            border: 'none',
          }}
        >Build briefing →</button>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
          textAlign: 'center',
        }}>share watchlist · by link or shared workspace</div>
      </footer>
    </aside>
  );
}

function PinChipWithGlyph({ pin }: { pin: Pin }) {
  const family = (FAM[pin.pathogen] ?? 'respiratory') as PathogenFamily;
  const color = IDC[pin.pathogen] ?? 'var(--bm-text-muted)';
  const tierColor = TIER_META[pin.tier].cssVar;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', left: 30, top: 8, zIndex: 1,
      }}>
        <FamilyGlyph family={family} color={color} size={13} />
      </div>
      <div style={{ paddingLeft: 18 }}>
        <PinChip type={pin.type} label={pin.label} stale={pin.stale} asOfDays={pin.asOfDays} tierColor={tierColor} />
      </div>
    </div>
  );
}
